import { useState } from "react";
import { Link2, Search, CheckCircle2, Loader2, AlertCircle, ExternalLink, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePageMeta } from "@/hooks/usePageMeta";
import type { BacklinkOpportunitiesResult, BacklinkOpportunity } from "@/types/analysis";

type Step = "crawling-user" | "crawling-competitor" | "analyzing" | "complete";

const steps: { key: Step; label: string }[] = [
  { key: "crawling-user", label: "Crawling your website" },
  { key: "crawling-competitor", label: "Crawling competitor website" },
  { key: "analyzing", label: "Analyzing backlink opportunities" },
];

const authorityColors: Record<string, string> = {
  high: "bg-success/15 text-success border-success/30",
  medium: "bg-warning/15 text-warning border-warning/30",
  low: "bg-muted text-muted-foreground border-border",
};

const difficultyColors: Record<string, string> = {
  easy: "bg-success/15 text-success border-success/30",
  medium: "bg-warning/15 text-warning border-warning/30",
  hard: "bg-destructive/15 text-destructive border-destructive/30",
};

function OpportunityCard({ opp }: { opp: BacklinkOpportunity }) {
  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center gap-1.5">
          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="font-medium text-sm truncate">{opp.domain}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className={authorityColors[opp.authorityEstimate]}>
            Authority: {opp.authorityEstimate}
          </Badge>
          <Badge variant="outline" className={authorityColors[opp.relevance]}>
            Relevance: {opp.relevance}
          </Badge>
          <Badge variant="outline" className={difficultyColors[opp.outreachDifficulty]}>
            {opp.outreachDifficulty}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">{opp.linkType}</p>
        <p className="text-sm">{opp.suggestedApproach}</p>
      </CardContent>
    </Card>
  );
}

export default function BacklinkOpportunitiesPage() {
  const { toast } = useToast();
  const [userUrl, setUserUrl] = useState("");
  const [competitorUrl, setCompetitorUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BacklinkOpportunitiesResult | null>(null);

  usePageMeta({
    title: "Backlink Opportunities — Unranked",
    description: "Find backlink opportunities by comparing your site against a competitor.",
    canonical: "https://unrankedagency.com/backlink-opportunities",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userUrl.trim() || !competitorUrl.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setCompletedSteps([]);

    setCurrentStep(0);
    const stepTimer1 = setTimeout(() => {
      setCompletedSteps([0]);
      setCurrentStep(1);
    }, 3000);
    const stepTimer2 = setTimeout(() => {
      setCompletedSteps([0, 1]);
      setCurrentStep(2);
    }, 6000);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("backlink-opportunities", {
        body: { userUrl: userUrl.trim(), competitorUrl: competitorUrl.trim() },
      });

      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);

      if (fnError) throw new Error(fnError.message);
      if (!data?.success) throw new Error(data?.error || "Analysis failed");

      setCompletedSteps([0, 1, 2]);
      setCurrentStep(3);
      setResult(data.data);
    } catch (err: any) {
      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);
      console.error("Backlink analysis error:", err);
      setError(err.message || "An unexpected error occurred");
      toast({ title: "Analysis failed", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setCurrentStep(-1);
    setCompletedSteps([]);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-full max-w-md mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Link2 className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-xl font-bold mb-1">Finding Backlink Opportunities</h2>
            <p className="text-sm text-muted-foreground">Comparing link profiles…</p>
          </div>
          <div className="space-y-3">
            {steps.map((step, i) => {
              const isComplete = completedSteps.includes(i);
              const isCurrent = currentStep === i;
              return (
                <div
                  key={step.key}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                    isComplete
                      ? "bg-success/10 border border-success/20"
                      : isCurrent
                      ? "bg-primary/10 border border-primary/20"
                      : "bg-card/50 border border-border/50 opacity-50"
                  }`}
                >
                  {isComplete ? (
                    <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
                  ) : isCurrent ? (
                    <Loader2 className="w-5 h-5 text-primary shrink-0 animate-spin" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border border-border shrink-0" />
                  )}
                  <span className={`text-sm font-medium ${isComplete ? "text-success" : isCurrent ? "text-foreground" : "text-muted-foreground"}`}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
          {error && (
            <div className="mt-6 text-center">
              <p className="text-sm text-destructive mb-4">{error}</p>
              <button onClick={reset} className="text-sm text-primary hover:underline">← Try again</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Results
  if (result) {
    return (
      <div className="space-y-6 max-w-5xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Backlink Opportunities</h1>
            <p className="text-sm text-muted-foreground mt-1">{result.summary}</p>
          </div>
          <Button variant="outline" onClick={reset} className="self-start sm:self-auto">New Comparison</Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-3xl font-bold text-primary">{result.totalFound}</p>
              <p className="text-sm text-muted-foreground">Opportunities Found</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-3xl font-bold text-success">{result.highValueCount}</p>
              <p className="text-sm text-muted-foreground">High Value</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-3xl font-bold text-warning">
                {result.opportunities.filter((o) => o.outreachDifficulty === "easy").length}
              </p>
              <p className="text-sm text-muted-foreground">Easy Wins</p>
            </CardContent>
          </Card>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden space-y-4">
          <h2 className="text-lg font-semibold">Opportunities</h2>
          <p className="text-sm text-muted-foreground">Domains you could target for backlinks based on your competitor's link profile</p>
          {result.opportunities.map((opp, i) => (
            <OpportunityCard key={i} opp={opp} />
          ))}
        </div>

        {/* Desktop table */}
        <Card className="hidden md:block">
          <CardHeader>
            <CardTitle className="text-lg">Opportunities</CardTitle>
            <CardDescription>Domains you could target for backlinks based on your competitor's link profile</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead>Authority</TableHead>
                    <TableHead>Relevance</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Difficulty</TableHead>
                    <TableHead className="min-w-[200px]">Suggested Approach</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.opportunities.map((opp, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-1.5">
                          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          {opp.domain}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={authorityColors[opp.authorityEstimate]}>{opp.authorityEstimate}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={authorityColors[opp.relevance]}>{opp.relevance}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{opp.linkType}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={difficultyColors[opp.outreachDifficulty]}>{opp.outreachDifficulty}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{opp.suggestedApproach}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Input form
  return (
    <div className="max-w-2xl mx-auto px-2 sm:px-0">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Backlink Opportunities</h1>
        <p className="text-muted-foreground">
          Compare your site against a competitor to discover backlink opportunities you're missing.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Your Website URL</label>
              <Input
                type="url"
                placeholder="https://yoursite.com"
                value={userUrl}
                onChange={(e) => setUserUrl(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Competitor URL</label>
              <Input
                type="url"
                placeholder="https://competitor.com"
                value={competitorUrl}
                onChange={(e) => setCompetitorUrl(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={!userUrl.trim() || !competitorUrl.trim()}>
              <Search className="w-4 h-4 mr-2" />
              Find Opportunities
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <div className="mt-4 text-center">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
    </div>
  );
}